import { z } from 'zod';
import { Country } from '@trnsact/trnsact-shared-types';

import { CustomerVisibility } from '../../enums';
import { equipmentStandardInfoSchema } from '../../../equipment/model';

const trimmedStr = z.string().trim();
const trimmedReqStr = z.string().trim().min(1, { message: 'Required field' });

const selectOptionSchema = z.array(z.string());

export const LineItemType = {
  Charge: 'CHARGE',
  Tax: 'TAX',
} as const;

export const AmountType = {
  Flat: 'flat',
  Percent: 'percent',
} as const;

const nullableNumber = z.preprocess(
  value => (value === '' || value === undefined || Number.isNaN(value) ? null : value),
  z.number().nullable()
);

const nullableUuid = z.preprocess(
  value => (value === '' || value === undefined ? null : value),
  z.string().uuid().nullable()
);

const capSchema = z.object({
  min: nullableNumber,
  max: nullableNumber,
});

const baseLineItemSchema = z.object({
  name: trimmedReqStr,
  id: z.string().uuid(),
  parentId: nullableUuid,
  description: trimmedStr,
  cap: capSchema.nullable(),
  isEquipmentLvl: z.boolean(),
  amount: z.number().nullable(),
  type: z.nativeEnum(LineItemType),
  amountType: z.nativeEnum(AmountType),
  sortOrder: z.number().int().nonnegative(),
  customerVisibility: z.nativeEnum(CustomerVisibility),
});

const equipmentSchema = z.object({ type: selectOptionSchema }).and(equipmentStandardInfoSchema);
const customerSchema = z.object({ type: selectOptionSchema, paymentType: selectOptionSchema }).partial();
const addressSchema = z.object({
  country: z.nativeEnum(Country),
  zip: z.array(trimmedStr).optional(),
  city: z.array(trimmedStr).optional(),
  state: selectOptionSchema.optional(),
  county: z.array(trimmedStr).optional(),
  province: selectOptionSchema.optional(),
  postalCode: z.array(trimmedStr).optional(),
});

const chargesSchema = validateParentRelation(baseLineItemSchema.extend({ isTaxable: z.boolean() }));
const taxesSchema = validateParentRelation(baseLineItemSchema);

export const salesCenterFormSchema = z.object({
  taxes: taxesSchema,
  name: trimmedReqStr,
  address: addressSchema,
  charges: chargesSchema,
  description: trimmedStr,
  customer: customerSchema,
  equipment: equipmentSchema,
});

export type DealSheetFormValues = z.infer<typeof salesCenterFormSchema>;
export type LineItemSection = 'charges' | 'taxes';

function validateParentRelation<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.array(itemSchema).superRefine((items, ctx) => {
    const byId = new Map(items.map(item => [item.id, item]));

    const hasCycle = (startId: string) => {
      const visited = new Set<string>();

      let currentId: string | null = startId;

      while (currentId) {
        if (visited.has(currentId)) return true;
        visited.add(currentId);
        currentId = byId.get(currentId)?.parentId ?? null;
      }

      return false;
    };

    items.forEach((item, index) => {
      const addParentError = (message: string) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'parentId'], message });
      };

      if (item.customerVisibility !== CustomerVisibility.InternalOnly) {
        if (item.parentId !== null) addParentError('Only "Internal only" items can have a parent');
        return;
      }

      if (!item.parentId) return;
      if (item.parentId === item.id) {
        addParentError('Item cannot reference itself as a parent');
        return;
      }
      if (!byId.has(item.parentId)) {
        addParentError('Parent must reference an existing item');
        return;
      }
      const parent = byId.get(item.parentId);
      if (parent && parent.type !== item.type) {
        addParentError('Parent must be of the same type');
        return;
      }
      if (hasCycle(item.id)) {
        addParentError('Parent relation creates a cycle');
      }
    });
  });
}
